import React, { useEffect, useCallback, useMemo } from "react";
import { Box } from "@mui/material";
import { Formik, Form, FieldArray } from "formik";
import { Node, Edge } from "@xyflow/react";
import ProcessToolbar from "./components/ProcessToolbar";
import FlowCanvas from "./components/FlowCanvas";
import StepDetailsPanel from "./components/StepDetailsPanel";
import { StepNodeData } from "./components/StepNode";
import { stepsToFlow, autoLayoutNodes } from "./util/flowTransform";
import { getStepInitialValues, FormValues } from "./Process";
import { ProcessResponse, StepCreateRequest } from "api/main/data-contracts";
import { MetaInformation } from "./Process";

// Step types that should not be available for adding new steps
const HIDDEN_STEP_TYPES = ["PAYMENT", "NOTIFICATION", "DATA_OUTPUT"];

interface ProcessVisualBuilderProps {
  process?: ProcessResponse;
  metaInfo?: MetaInformation;
  onSubmit: (values: FormValues) => Promise<void>;
  onExport: (values: FormValues) => void;
  onImport: (setValues: any, setFieldValue: any) => Promise<void>;
  isNewProcess?: boolean;
}

interface FormContentProps {
  metaInfo?: MetaInformation;
  onExport: (values: FormValues) => void;
  onImport: (setValues: any, setFieldValue: any) => Promise<void>;
  values: FormValues;
  isValid: boolean;
  dirty: boolean;
  setValues: any;
  setFieldValue: any;
  submitForm: () => Promise<void>;
}

function FormContent({
  metaInfo,
  onExport,
  onImport,
  values,
  isValid,
  dirty,
  setValues,
  setFieldValue,
  submitForm,
}: FormContentProps) {
  const [selectedNodeId, setSelectedNodeId] = React.useState<string | null>(null);
  const [nodes, setNodes] = React.useState<Node<StepNodeData>[]>([]);
  const [edges, setEdges] = React.useState<Edge[]>([]);

  console.log("[ProcessVisualBuilder] metaInfo:", metaInfo);
  console.log("[ProcessVisualBuilder] stepTypes:", metaInfo?.stepTypes);

  // Filter out hidden step types
  const availableStepTypes = (metaInfo?.stepTypes || []).filter(
    (type) => !HIDDEN_STEP_TYPES.includes(type)
  );

  // Handlers for node operations
  const handleEditNode = useCallback((stepIndex: number) => {
    setSelectedNodeId(`step-${stepIndex}`);
  }, []);

  const handleDeleteNode = useCallback(
    (stepIndex: number) => {
      if (window.confirm(`Ar tikrai norite ištrinti ${stepIndex + 1} žingsnį?`)) {
        const newSteps = values.steps?.filter((_, idx) => idx !== stepIndex);
        setFieldValue("steps", newSteps);
        setSelectedNodeId(null);
      }
    },
    [values.steps, setFieldValue]
  );

  const handleAddStep = useCallback(
    (stepType: string) => {
      const newStep = getStepInitialValues(undefined, values.steps?.length ?? 0);
      newStep.type = stepType as any;
      setFieldValue("steps", [...(values.steps ?? []), newStep]);
    },
    [values.steps, setFieldValue]
  );

  // Update flow visualization when steps change
  useEffect(() => {
    const { nodes: newNodes, edges: newEdges } = stepsToFlow(
      values.steps ?? [],
      handleEditNode,
      handleDeleteNode
    );
    setNodes(newNodes);
    setEdges(newEdges);
  }, [values.steps, handleEditNode, handleDeleteNode]);

  // Handle node order change from flow
  const handleNodesChange = useCallback(
    (updatedNodes: Node<StepNodeData>[]) => {
      // Extract original step indices from node IDs (format: "step-{originalIndex}")
      // and map them to actual steps based on their new order
      const reorderedSteps = updatedNodes.map((node) => {
        // Parse original index from node ID
        const originalIndex = parseInt(node.id.replace("step-", ""));
        return values.steps?.[originalIndex];
      });

      // Filter out any undefined values
      const validSteps = reorderedSteps.filter((step): step is StepCreateRequest => step !== undefined);

      if (validSteps.length > 0) {
        setFieldValue("steps", validSteps);
      }
    },
    [values.steps, setFieldValue]
  );

  const handleZoomIn = useCallback(() => {
    // Handled by ReactFlow Controls
  }, []);

  const handleZoomOut = useCallback(() => {
    // Handled by ReactFlow Controls
  }, []);

  const handleFitView = useCallback(() => {
    // Handled by ReactFlow Controls
  }, []);

  const handleAutoLayout = useCallback(() => {
    const layoutedNodes = autoLayoutNodes(nodes);
    setNodes(layoutedNodes);
  }, [nodes]);

  const handleClosePanel = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  const handleNameChange = useCallback(
    (name: string) => {
      setFieldValue("name", name);
    },
    [setFieldValue]
  );

  const handleActiveChange = useCallback(
    (active: boolean) => {
      setFieldValue("active", active);
    },
    [setFieldValue]
  );

  const handleAIGenerate = useCallback(
    (name: string, steps: any[]) => {
      console.log("[ProcessVisualBuilder] AI generated process:", name, steps);
      // Log each step's inputContent and validations
      steps.forEach((step, idx) => {
        console.log(`[ProcessVisualBuilder] Step ${idx}:`, JSON.stringify(step, null, 2));
        if (step.stepInformation?.inputContent) {
          step.stepInformation.inputContent.forEach((group: any[], gIdx: number) => {
            group.forEach((field: any, fIdx: number) => {
              console.log(`[ProcessVisualBuilder] Step ${idx} Group ${gIdx} Field ${fIdx}:`, field.name, "validations:", field.validations);
            });
          });
        }
      });
      setFieldValue("name", name);
      setFieldValue("steps", steps);
    },
    [setFieldValue]
  );

  const selectedStepIndex = selectedNodeId
    ? parseInt(selectedNodeId.replace("step-", ""))
    : null;

  const selectedStep =
    selectedStepIndex !== null && values.steps ? values.steps[selectedStepIndex] : null;

  return (
    <Form style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
        {/* Toolbar */}
        <ProcessToolbar
          processName={values.name ?? ""}
          isActive={values.active ?? false}
          onSave={submitForm}
          onExport={() => onExport(values)}
          onImport={() => onImport(setValues, setFieldValue)}
          onAddStep={() => handleAddStep("DATA_INPUT")}
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onFitView={handleFitView}
          onAutoLayout={handleAutoLayout}
          isDirty={dirty}
          isValid={isValid}
          onNameChange={handleNameChange}
          onActiveChange={handleActiveChange}
          availableStepTypes={availableStepTypes}
          onAIGenerate={handleAIGenerate}
        />

        {/* Main content area */}
        <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
          {/* Center - Flow canvas */}
          <FieldArray name="steps">
            {() => (
              <FlowCanvas
                initialNodes={nodes}
                initialEdges={edges}
                onNodesChange={handleNodesChange}
                onNodeSelect={setSelectedNodeId}
                selectedNodeId={selectedNodeId}
                onAddStep={handleAddStep}
                availableStepTypes={availableStepTypes}
              />
            )}
          </FieldArray>

          {/* Right panel - Step details */}
          <StepDetailsPanel
            selectedStepIndex={selectedStepIndex}
            step={selectedStep}
            metaInfo={metaInfo}
            onClose={handleClosePanel}
            values={values}
          />
        </Box>
      </Box>
    </Form>
  );
}

export default function ProcessVisualBuilder({
  process,
  metaInfo,
  onSubmit,
  onExport,
  onImport,
  isNewProcess: _isNewProcess,
}: ProcessVisualBuilderProps) {
  const initialValues: FormValues = useMemo(() => {
    return {
      i18nkey: process?.i18nkey ?? "",
      name: process?.name ?? "",
      active: process?.active ?? false,
      steps:
        process?.steps?.map((step) => getStepInitialValues(step)) ?? [],
    };
  }, [process]);

  return (
    <Formik
      initialValues={initialValues}
      validateOnChange={false}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({ values, isValid, dirty, setValues, setFieldValue, submitForm }) => (
        <FormContent
          metaInfo={metaInfo}
          onExport={onExport}
          onImport={onImport}
          values={values}
          isValid={isValid}
          dirty={dirty}
          setValues={setValues}
          setFieldValue={setFieldValue}
          submitForm={submitForm}
        />
      )}
    </Formik>
  );
}
